import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import axios from '../axios';
import KpiCardGroup from './shared/KpiCardGroup';
import PeriodSelector from './shared/PeriodSelector';
import TimeSeriesChart from './shared/TimeSeriesChart';
import EventComparisonTable from './circle/EventComparisonTable';
import PrPointManagement from './circle/PrPointManagement';

const CHART_TABS = [
  { key: 'members', label: 'メンバー推移' },
  { key: 'pv', label: 'PV推移' },
  { key: 'events', label: 'イベント比較' },
];

const CircleDashboard = ({ circleId, purchaseUrl }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);
  const [activeChart, setActiveChart] = useState('members');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/dashboard/circle/${circleId}?period=${period}`);
      setData(res.data);
    } catch (e) {
      console.error('Dashboard fetch error:', e);
      setError('データの取得に失敗しました。ページを再読み込みしてください。');
    }
    setIsLoading(false);
  }, [circleId, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (isLoading) {
    return <div className="o-dashboard-loading">読み込み中...</div>;
  }

  if (error || !data) {
    return (
      <div className="o-dashboard-loading">
        <div>
          <p>{error || 'データを取得できませんでした。'}</p>
          <button type="button" onClick={fetchData} className="a-button a-button--primary">
            再読み込み
          </button>
        </div>
      </div>
    );
  }

  const { kpi, charts, pr_points } = data;

  const kpiCards = [
    { label: 'メンバー数', value: kpi.members.value, diff: kpi.members.diff, rate: kpi.members.rate },
    { label: `${period}日間PV`, value: kpi.pageviews.value, diff: kpi.pageviews.diff, rate: kpi.pageviews.rate },
    { label: '参加率', value: kpi.join_rate.value, diff: kpi.join_rate.diff, rate: kpi.join_rate.rate, format: 'percent' },
    { label: '売上合計', value: kpi.revenue.value, diff: kpi.revenue.diff, rate: kpi.revenue.rate, format: 'currency' },
    { label: 'PRポイント残高', value: kpi.pr_balance.value },
  ];

  const renderChart = () => {
    switch (activeChart) {
      case 'members':
        return (
          <TimeSeriesChart
            data={charts.members_trend}
            lines={[{ dataKey: 'value', name: 'メンバー数' }]}
          />
        );
      case 'pv':
        return (
          <TimeSeriesChart
            data={charts.pv_trend}
            bars={[{ dataKey: 'value', name: 'PV' }]}
          />
        );
      case 'events':
        return <EventComparisonTable events={charts.events_comparison} />;
      default:
        return null;
    }
  };

  return (
    <div className="o-dashboard">
      <div className="o-dashboard__header">
        <h2 className="o-dashboard__title">サークルダッシュボード</h2>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      <KpiCardGroup cards={kpiCards} />

      <div className="o-dashboard__section">
        <div className="o-chart-tabs">
          {CHART_TABS.map((tab) => (
            <button
              type="button"
              key={tab.key}
              className={`o-chart-tabs__tab${activeChart === tab.key ? ' o-chart-tabs__tab--active' : ''}`}
              onClick={() => setActiveChart(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {renderChart()}
      </div>

      <PrPointManagement
        balance={pr_points.balance}
        events={pr_points.events}
        purchaseUrl={purchaseUrl}
        onDataChange={fetchData}
      />
    </div>
  );
};

CircleDashboard.propTypes = {
  circleId: PropTypes.number.isRequired,
  purchaseUrl: PropTypes.string,
};

export default CircleDashboard;
